import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ReactInputMask from "react-input-mask";
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";

import FinishOrderButton from "../../components/finishOrderButton/finishOrderButton";
import SelectField from "../../../../components/select/select";

import "./step-3.scss";
import api from "../../../../services/api";
import { CartContext } from "../../../../contexts/cartContext";
import { LoadingContext } from "../../../../contexts/loadingContext";
import { FranchiseContext } from "../../../../contexts/franchiseContext";
import Swal from "sweetalert2";

const schema = yup.object().shape({
  pagamento: yup
    .string()
    .required("Essa forma de pagamento não está disponível para esse pedido."),
  nomeCartao: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () => yup.string().required("Nome do cartão é obrigatório."),
    otherwise: () => yup.string(),
  }),
  numeroCartao: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () =>
      yup
        .string()
        .matches(/^\d{4}\s\d{4}\s\d{4}\s\d{4}$/, "Número de cartão inválido")
        .required("Número de cartão é obrigatório"),
    otherwise: () => yup.string(),
  }),
  mesCartao: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () => yup.string().required("Selecione o mês de validade do cartão."),
    otherwise: () => yup.string(),
  }),

  anoCartao: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () => yup.string().required("Selecione o ano de validade do cartão."),
    otherwise: () => yup.string(),
  }),

  cvc: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () => yup.string().required("O campo CVC é obrigatório."),
    otherwise: () => yup.string(),
  }),
  parcelas: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () =>
      yup.string().required("Selecione quantas parcelas de pagamento."),
    otherwise: () => yup.string(),
  }),
  titularCPF: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () =>
      yup
        .string()
        .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "CPF inválido")
        .required("Insira os dados do titular."),
    otherwise: () => yup.string(),
  }),
  titularNascimento: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () =>
      yup
        .string()
        .matches(
          /^(0[1-9]|1[0-9]|2[0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
          "Formato de data inválido (dd/mm/aaaa)"
        )
        .required("Insira o nascimento do titular."),
    otherwise: () => yup.string(),
  }),
  titularTelefone: yup.string().when("pagamento", {
    is: "Pagseguro",
    then: () => yup.string().required("Insira o telefone do titular."),
    otherwise: () => yup.string(),
  }),
});

const monthOptions = [
  {
    label: "01 - Janeiro",
    value: 1,
  },
  {
    label: "02 - Fevereiro",
    value: 2,
  },
  {
    label: "03 - Março",
    value: 3,
  },
  {
    label: "04 - Abril",
    value: 4,
  },
  {
    label: "05 - Maio",
    value: 5,
  },
  {
    label: "06 - Junho",
    value: 6,
  },
  {
    label: "07 - Julho",
    value: 7,
  },
  {
    label: "08 - Agosto",
    value: 8,
  },
  {
    label: "09 - Setembro",
    value: 9,
  },
  {
    label: "10 - Outubro",
    value: 10,
  },
  {
    label: "11 - Novembro",
    value: 11,
  },
  {
    label: "12 - Dezembro",
    value: 12,
  },
];

const yearsOptions = [
  { label: 2023, value: 2023 },
  { label: 2024, value: 2024 },
  { label: 2025, value: 2025 },
  { label: 2026, value: 2026 },
  { label: 2027, value: 2027 },
  { label: 2028, value: 2028 },
  { label: 2029, value: 2029 },
];

let calculateFeesTimeout;
function Step3({ prevStep, nextStep, formData, onFuncaoDoBotaoExecutada }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [installments, setInstallments] = useState([]);

  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);

  const { cartSubtotal } = useContext(CartContext);
  const { setIsLoadingGlobal } = useContext(LoadingContext);
  const { franchise } = useContext(FranchiseContext);

  const cardNumberInputprops = { ...register("numeroCartao") };

  useEffect(() => {
    api.get("payment-types").then((response) => {
      setPaymentTypes(response.data);
    });
  }, []);

  const handleRadioChange = (element) => {
    console.log("Element,", element.value);
    setSelectedOption(element.value);
  };

  const onSubmit = async (data) => {
    if (!isTermsAccepted) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Você precisa aceitar os termos de uso para realizar a compra!",
      });
      return;
    }
    let newData = { ...formData, ...data, selectedOption };
    nextStep(newData);
    onFuncaoDoBotaoExecutada();
  };

  const calculateFees = (value, credit_card_bin) => {
    clearTimeout(calculateFeesTimeout);
    calculateFeesTimeout = setTimeout(async () => {
      setIsLoadingGlobal(true);
      try {
        const response = await api.get(
          `calculate/fees?value=${value}&credit_card_bin=${credit_card_bin}&franchise_id=${franchise.id}`
        );
        const creditCardFees = response.data.payment_methods.credit_card;
        const cardFees = creditCardFees[Object.keys(creditCardFees)[0]];
        const mappedInstallments = cardFees.installment_plans.map((plan) => {
          return {
            label: `${plan.installments} - Parcelas de ${formatToBRL(
              plan.amount.value
            )}`,
            value: plan.installments,
          };
        });

        setInstallments(mappedInstallments);
      } catch (error) {
        console.log("Error", error);
        Swal.fire({
          title: "Error!",
          text: "Cartão inválido, por favor digite novamente!",
          icon: "error",
        });
      } finally {
        setIsLoadingGlobal(false);
      }
    }, 1000);
  };

  function formatToBRL(cents) {
    const reais = cents / 100;

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(reais);
  }

  return (
    <div>
      <form className="form step-3" onSubmit={handleSubmit(onSubmit)}>
        <div className="row form-title mb-4">
          <h1>Selecione uma forma de pagamento</h1>
        </div>
        <div className="payments">
          {paymentTypes.map((paymentType) => {
            if (paymentType.name === 'Pagseguro' && !franchise.is_pagseguro_enabled) {
              return null;
            }
            return (
              <>
                <div
                  className={`form-radio-control w-100 d-flex flex-column mb-3 p-2 ${selectedOption === paymentType.name ? "selected" : "hidden"
                    }`}
                  style={isMobile ? { lineHeight: 1.4 } : { lineHeight: 1.2 }}
                >
                  <div className="d-flex">
                    <input
                      className="radio-big"
                      type="radio"
                      id={paymentType.name}
                      value={paymentType.name}
                      defaultChecked={
                        formData.pagamento === paymentType.name ? true : false
                      }
                      {...register("pagamento")}
                      onClick={(e) => handleRadioChange(e.target)}
                    />
                    <div className="label-box ms-2">
                      <h1
                        style={
                          isMobile ? { lineHeight: 1.4 } : { lineHeight: 1.2 }
                        }
                      >
                        {paymentType.name}
                      </h1>
                      {paymentType.name === "Pagseguro" && (
                        <h4
                          style={
                            isMobile ? { lineHeight: 1.4 } : { lineHeight: 1.2 }
                          }
                        >
                          Utilize seu cartão de crédito para efetuar o
                          pagamento.
                        </h4>
                      )}

                      {paymentType.name === "PicPay" && (
                        <h4
                          style={
                            isMobile ? { lineHeight: 1.4 } : { lineHeight: 1.2 }
                          }
                        >
                          Utilize seu saldo da carteira Picpay ou seu cartão de
                          crédito para efetuar o pagamento.
                        </h4>
                      )}

                      {paymentType.name === "PIX/TED" && (
                        <h4
                          style={
                            isMobile ? { lineHeight: 1.4 } : { lineHeight: 1.2 }
                          }
                        >
                          Utilize o aplicativo do seu banco para efetuar o
                          pagamento, logo depois envie o comprovante no prazo de
                          até 6 horas.
                        </h4>
                      )}
                    </div>
                  </div>
                  {paymentType.name === "Pagseguro" ? (
                    <>
                      <div
                        className={`pag-safety ${isMobile ? "px-2" : "px-4"}`}
                      >
                        <h1 className="title mb-3">
                          Insira os dados do cartão de crédito
                        </h1>
                        <div className="row">
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-6"
                              }`}
                          >
                            <label className="pag-safety-form-label">
                              Nome impresso no cartão
                            </label>
                            <input
                              type="text"
                              className="pag-safety-form-control form-control"
                              placeholder="Digite o nome que está impresso no cartão"
                              {...register("nomeCartao")}
                            />
                            {errors.nomeCartao ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.nomeCartao.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-6"
                              }`}
                          >
                            <label className="pag-safety-form-label">
                              Número do cartão
                            </label>
                            <ReactInputMask
                              className="pag-safety-form-control form-control"
                              mask="9999 9999 9999 9999"
                              placeholder="Digite o número do cartão"
                              maskChar={null}
                              defaultValue={
                                formData.numeroCartao
                                  ? formData.numeroCartao
                                  : ""
                              }
                              {...cardNumberInputprops}
                              onChange={(e) => {
                                const cleanedValue = e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                                cardNumberInputprops.onChange(e);
                                if (cleanedValue.length > 5) {
                                  const first6Chars = cleanedValue.substring(
                                    0,
                                    6
                                  );
                                  calculateFees(cartSubtotal, first6Chars);
                                  console.log("first6Chars", first6Chars);
                                } else {
                                  clearTimeout(calculateFeesTimeout);
                                }
                              }}
                            >
                              {(inputProps) => <input {...inputProps} />}
                            </ReactInputMask>
                            {errors.numeroCartao ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.numeroCartao.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                        <div className="row">
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-6"
                              }`}
                          >
                            <label className="pag-safety-form-label">
                              Validade do cartão
                            </label>
                            <SelectField
                              label={"Mês (validade)"}
                              defaultValue={
                                formData.mesCartao ? formData.mesCartao : ""
                              }
                              options={monthOptions}
                              register={register}
                              name={"mesCartao"}
                            />
                            {errors.mesCartao ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.mesCartao.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-3"
                              }`}
                            style={
                              isMobile
                                ? { marginTop: "0px" }
                                : { marginTop: "27px" }
                            }
                          >
                            <SelectField
                              label={"Ano"}
                              defaultValue={
                                formData.anoCartao ? formData.anoCartao : ""
                              }
                              options={yearsOptions}
                              register={register}
                              name={"anoCartao"}
                            />
                            {errors.anoCartao ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.anoCartao.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-3"
                              }`}
                          >
                            <label className="pag-safety-form-label">CVC</label>
                            <input
                              type="number"
                              className="pag-safety-form-control form-control"
                              placeholder="Código de segurança"
                              {...register("cvc")}
                            />
                            {errors.cvc ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.cvc.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                        {installments.length > 0 ? (
                          <div className="row">
                            <div className="d-flex flex-column w-100 mb-3">
                              <label className="pag-safety-form-label">
                                Em quantas parcelas deseja pagar?
                              </label>
                              <SelectField
                                label={"Parcelas"}
                                defaultValue={
                                  formData.parcelas ? formData.parcelas : ""
                                }
                                options={installments}
                                register={register}
                                name={"parcelas"}
                              />
                              {errors.parcelas ? (
                                <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    className="bi bi-x-circle-fill"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                  </svg>
                                  <p>{errors.parcelas.message}</p>
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                        ) : null}
                        <h1 className="title mb-3 mt-3">
                          Insira os dados do titular do cartão
                        </h1>
                        <div className="row">
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-4"
                              }`}
                          >
                            <label className="pag-safety-form-label">
                              CPF do titular do cartão
                            </label>
                            <ReactInputMask
                              className="pag-safety-form-control form-control"
                              mask="999.999.999-99"
                              placeholder="Digite o CPF do titular"
                              maskChar={null}
                              defaultValue={
                                formData.titularCPF ? formData.titularCPF : ""
                              }
                              {...register("titularCPF")}
                            >
                              {(inputProps) => <input {...inputProps} />}
                            </ReactInputMask>
                            {errors.titularCPF ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.titularCPF.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-4"
                              }`}
                          >
                            <label className="pag-safety-form-label">
                              Data de nascimento
                            </label>
                            <ReactInputMask
                              className="pag-safety-form-control form-control"
                              mask="99/99/9999"
                              placeholder="Nascimento do titular"
                              maskChar={null}
                              defaultValue={
                                formData.titularNascimento
                                  ? formData.titularNascimento
                                  : ""
                              }
                              {...register("titularNascimento")}
                            >
                              {(inputProps) => <input {...inputProps} />}
                            </ReactInputMask>
                            {errors.titularNascimento ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.titularNascimento.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                          <div
                            className={`d-flex flex-column mb-3 ${isMobile ? "" : "col-4"
                              }`}
                          >
                            <label className="pag-safety-form-label">
                              Telefone do titular
                            </label>
                            <ReactInputMask
                              className="pag-safety-form-control form-control"
                              mask="(99) 99999-9999"
                              placeholder="DDD + telefone"
                              maskChar={null}
                              defaultValue={
                                formData.titularTelefone
                                  ? formData.titularTelefone
                                  : ""
                              }
                              {...register("titularTelefone")}
                            >
                              {(inputProps) => <input {...inputProps} />}
                            </ReactInputMask>
                            {errors.titularTelefone ? (
                              <div className="w-100 d-flex align-items-center gap-2 mt-4 validation-control">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="currentColor"
                                  className="bi bi-x-circle-fill"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                </svg>
                                <p>{errors.titularTelefone.message}</p>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                          <div className="pag-safety-warning">
                            <label className="form-check-label warning mt-4">
                              <strong>
                                Aviso importante! As informações acima
                                solicitadas são enviadas diretamente e
                                exclusivamente para o Pagseguro. Assim nenhuma
                                informação é armazenada em nosso site.
                              </strong>
                            </label>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            );
          })}
          {errors.pagamento ? (
            <div className="w-100 d-flex align-items-center gap-2 mb-3 validation-control">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-x-circle-fill"
                viewBox="0 0 16 16"
              >
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
              </svg>
              <p>{errors.pagamento.message}</p>
            </div>
          ) : (
            ""
          )}
        </div>
        {isMobile ? (
          <div className="w-100 mb-4 d-flex flex-column buttons mobile">
            <FinishOrderButton />
          </div>
        ) : (
          ""
        )}
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="exampleCheck1"
            onChange={(e) => setIsTermsAccepted(e.target.checked)}
          />
          <label className="form-check-label">
            Concordo com os<> </>
            <strong>
              <Link>Termos de Uso</Link>
            </strong>
            <> </>e com nossa<> </>
            <strong>
              <Link>Política de Privacidade de Proteção de Dados.</Link>
            </strong>
          </label>
        </div>
        <div
          className={`w-100 d-flex ${isMobile ? "flex-column mobile pb-5" : ""
            } justify-content-between buttons`}
        >
          <button
            type="button"
            className={`btn button-control button-prev ${isMobile
                ? "mt-4 d-flex align-items-center justify-content-center"
                : "mt-4"
              }`}
            style={isMobile ? { height: "38px" } : {}}
            onClick={() => prevStep()}
          >
            Voltar
          </button>
          <button
            id="buttonSubmitStep3"
            type="submit"
            style={{ display: "none" }}
          >
            Confirmar
          </button>
        </div>
      </form>
    </div>
  );
}

export default Step3;
